import { useCallback, useMemo, useState } from 'react';

import {
  AppBar,
  Box,
  Button,
  ButtonGroup,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { useModal } from 'react-modal-hook';

import AppliedFilters from 'components/AppliedFilters';
import FilterButton from 'components/FilterButton';
import SearchBar from 'components/SearchBar';
import TabPanel from 'components/TabPanel';
import useAuth from 'hooks/useAuth';
import useFilters from 'hooks/useFilters';
import { IFiltersOptions } from 'types';

import ModalFilters, {
  defaultModalFilters,
  mapModalFiltersToChips,
  ModalFilterOptionsType,
} from './components/ModalFilters';
import TabPanelAtendimentosEmAndamento from './components/TabPanelAtendimentosEmAndamento';
import TabPanelAtendimentosFinalizados from './components/TabPanelAtendimentosFinalizados';

export default function Historico() {
  const { currentUser } = useAuth();

  const defaultState = useMemo<ModalFilterOptionsType>(
    () => ({
      ...defaultModalFilters,
      participantId: currentUser ? currentUser.id : undefined,
      atendente: currentUser
        ? {
            id: currentUser.id,
            name: currentUser.nome,
            email: currentUser.email,
            foto: currentUser.foto,
            descricao: '',
            isOnline: true,
          }
        : undefined,
    }),
    [currentUser],
  );

  const [appliedFilters, setAppliedFilters] =
    useState<ModalFilterOptionsType>(defaultState);

  const initialChips = useMemo(() => {
    return mapModalFiltersToChips(appliedFilters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    filters: chips,
    setFilters,
    onRemoveFilter,
  } = useFilters(initialChips);

  const [tabValue, setTabValue] = useState(0);

  const [inputValue, setInputValue] = useState(appliedFilters.message || '');

  const handleApplyFilters = useCallback(
    (filters: ModalFilterOptionsType, newChips: IFiltersOptions[]) => {
      setAppliedFilters(filters);
      setFilters(newChips);
    },
    [setAppliedFilters, setFilters],
  );

  const handleRemoveFilter = useCallback(
    (property: string) => {
      const updates: Partial<ModalFilterOptionsType> = {};
      if (property === 'tags') {
        updates.tagIds = [];
        updates.selectedTags = [];
        onRemoveFilter('tags');
      } else if (property === 'rangeDateCreatedAt') {
        updates.rangeDateCreatedAt = [];
        onRemoveFilter('rangeDateCreatedAt');
      } else if (property === 'rangeDate') {
        updates.rangeDate = [];
        onRemoveFilter('rangeDate');
      } else if (property === 'code') {
        updates.code = undefined;
        onRemoveFilter('code');
      } else if (property === 'participantId') {
        updates.participantId = undefined;
        updates.atendente = undefined;
        onRemoveFilter('participantId');
      } else if (property === 'channelCodes') {
        updates.channelCodes = [];
        updates.canal = undefined;
        onRemoveFilter('channelCodes');
      } else if (property === 'contatoId') {
        updates.contatoId = undefined;
        updates.contato = undefined;
        onRemoveFilter('contatoId');
      } else if (property === 'reasonId') {
        updates.reasonId = undefined;
        updates.reason = undefined;
        onRemoveFilter('reasonId');
      }

      setAppliedFilters({
        ...appliedFilters,
        ...updates,
      } as ModalFilterOptionsType);
    },
    [appliedFilters, onRemoveFilter, setAppliedFilters],
  );

  const handleResetFilters = useCallback(() => {
    const defaultState: ModalFilterOptionsType = {
      ...defaultModalFilters,
    };

    setInputValue('');
    setAppliedFilters(defaultState);
    setFilters(mapModalFiltersToChips(defaultState));
  }, [setAppliedFilters, setFilters]);

  const handleChangeSearch = useCallback(
    (value: string) => {
      setInputValue(value);
      setAppliedFilters({ ...appliedFilters, message: value });
    },
    [appliedFilters, setAppliedFilters],
  );

  const handleCancelSearch = useCallback(() => {
    setInputValue('');
    handleChangeSearch('');
  }, [handleChangeSearch]);

  const [showModalFilters, hideModalFilters] = useModal(
    ({ in: open }) => (
      <ModalFilters
        open={open}
        onClose={hideModalFilters}
        initialFilters={appliedFilters}
        onApply={handleApplyFilters}
        onClear={handleResetFilters}
        isFinalizado={tabValue === 0}
      />
    ),
    [appliedFilters, handleApplyFilters, handleResetFilters, tabValue],
  );

  return (
    <Box height="100%" overflow="hidden" display="flex" flexDirection="column">
      <AppBar position="relative" color="transparent" elevation={0}>
        <Toolbar style={{ minHeight: 48 }} disableGutters>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="h6">Histórico de atendimentos</Typography>

            <Box display="flex" alignItems="center" gridGap={8}>
              <ButtonGroup>
                <Button
                  disableElevation
                  color={tabValue === 0 ? 'primary' : 'default'}
                  variant={tabValue === 0 ? 'contained' : 'outlined'}
                  onClick={() => setTabValue(0)}
                >
                  Finalizados
                </Button>
                <Button
                  color={tabValue === 1 ? 'primary' : 'default'}
                  disableElevation
                  variant={tabValue === 1 ? 'contained' : 'outlined'}
                  onClick={() => {
                    setTabValue(1);
                    handleRemoveFilter('rangeDate');
                  }}
                >
                  Em andamento
                </Button>
              </ButtonGroup>
              <Box flex={1} minWidth={280}>
                <SearchBar
                  onChange={handleChangeSearch}
                  onCancelSearch={handleCancelSearch}
                  placeholder="Procurar por mensagem"
                  value={inputValue}
                  showSearchIcon
                />
              </Box>

              <FilterButton onClick={showModalFilters} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box height="100%" overflow="hidden" bgcolor="background.default">
        <TabPanel paddingTop={0.5} index={0} value={tabValue}>
          {chips.length > 0 && (
            <AppliedFilters
              filters={chips}
              onRemove={handleRemoveFilter}
              onRemoveAll={handleResetFilters}
            />
          )}
          <TabPanelAtendimentosFinalizados
            appliedFilters={appliedFilters}
            isFilter={chips.length > 0}
          />
        </TabPanel>

        <TabPanel paddingTop={0.5} index={1} value={tabValue}>
          {chips.length > 0 && (
            <AppliedFilters
              filters={chips}
              onRemove={handleRemoveFilter}
              onRemoveAll={handleResetFilters}
            />
          )}

          <TabPanelAtendimentosEmAndamento
            appliedFilters={{ ...appliedFilters, rangeDate: [] }}
            isFilter={chips.length > 0}
          />
        </TabPanel>
      </Box>
    </Box>
  );
}
